import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, ShieldCheck } from "lucide-react";

import { adminSettingsApi } from "@profitlens/api-client";
import { Badge } from "@profitlens/ui/badge";
import { Button } from "@profitlens/ui/button";
import { Card } from "@profitlens/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@profitlens/ui/dialog";
import { ErrorState } from "@profitlens/ui/error-state";
import { Input } from "@profitlens/ui/input";
import { Label } from "@profitlens/ui/label";
import { Spinner } from "@profitlens/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@profitlens/ui/table";

import { PageHeader } from "@/components/page-header";
import { translateError } from "@/lib/api-error";
import { formatDateTime } from "@/lib/format";

export function AdminsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ["admin", "admins"],
    queryFn: () => adminSettingsApi.listAdmins(),
  });

  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["admin", "admins"] }),
      queryClient.invalidateQueries({ queryKey: ["admin", "audit-logs"] }),
    ]);

  const create = useMutation({
    mutationFn: () => adminSettingsApi.createAdmin({ email, password }),
    onSuccess: async () => {
      setAdding(false);
      setEmail("");
      setPassword("");
      setError(null);
      await invalidate();
    },
    onError: (err) => setError(translateError(err, t)),
  });

  const toggle = useMutation({
    mutationFn: ({ id, status }: { id: string; status: "active" | "disabled" }) =>
      adminSettingsApi.patchAdmin(id, { status }),
    onSuccess: invalidate,
    onError: (err) => setError(translateError(err, t)),
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title={t("ops.adminsTitle")}
        subtitle={t("ops.adminsSubtitle")}
        actions={
          <Button
            variant="outline"
            onClick={() => {
              setAdding(true);
              setEmail("");
              setPassword("");
              setError(null);
            }}
          >
            <Plus className="h-4 w-4" />
            {t("ops.adminAdd")}
          </Button>
        }
      />

      {error && !adding ? <p className="text-sm text-danger">{error}</p> : null}

      {list.isLoading ? (
        <div className="grid place-items-center py-12">
          <Spinner className="h-5 w-5" />
        </div>
      ) : list.error ? (
        <ErrorState error={list.error} onRetry={() => list.refetch()} />
      ) : list.data && list.data.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted">{t("ops.adminsEmpty")}</Card>
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("ops.adminEmail")}</TableHead>
                <TableHead>{t("common.role")}</TableHead>
                <TableHead>{t("common.status")}</TableHead>
                <TableHead>{t("common.lastLoginAt")}</TableHead>
                <TableHead>{t("common.createdAt")}</TableHead>
                <TableHead className="w-24">{t("common.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.data?.map((admin) => (
                <TableRow key={admin.id}>
                  <TableCell className="font-medium">
                    <span className="inline-flex items-center gap-1.5">
                      <ShieldCheck className="h-3.5 w-3.5 text-brand" />
                      {admin.email}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{admin.role}</Badge>
                  </TableCell>
                  <TableCell>
                    {admin.status === "active" ? (
                      <Badge variant="success">{t("ops.adminStatusActive")}</Badge>
                    ) : (
                      <Badge variant="danger">{t("ops.adminStatusDisabled")}</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-xs whitespace-nowrap text-muted">
                    {admin.last_login_at ? formatDateTime(admin.last_login_at) : t("common.never")}
                  </TableCell>
                  <TableCell className="text-xs whitespace-nowrap text-muted">
                    {formatDateTime(admin.created_at)}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={toggle.isPending}
                      onClick={() =>
                        toggle.mutate({
                          id: admin.id,
                          status: admin.status === "active" ? "disabled" : "active",
                        })
                      }
                    >
                      {admin.status === "active" ? t("ops.adminDisable") : t("ops.adminEnable")}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={adding} onOpenChange={(o) => !o && setAdding(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("ops.adminAdd")}</DialogTitle>
          </DialogHeader>
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              setError(null);
              create.mutate();
            }}
          >
            <div className="space-y-1.5">
              <Label>{t("ops.adminEmail")}</Label>
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t("ops.adminPassword")}</Label>
              <Input
                type="text"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <p className="text-xs text-muted">{t("ops.adminPasswordHint")}</p>
            </div>
            {error ? <p className="text-xs text-danger">{error}</p> : null}
            <DialogFooter>
              <Button variant="ghost" type="button" onClick={() => setAdding(false)}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={create.isPending || !email || password.length < 8}>
                {create.isPending ? t("common.processing") : t("common.create")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
